import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { useQuery, useDetail, useSet } from "seed/gql";
import { useUserUsage } from "components/util/helpers/UserUsage";
import { SET_FLOW } from "seed/gql/queries";
import { usePost, useGetCall } from "seed/api";
import { Loading } from "seed/helpers";
import InitialView from "components/presentation/Presentation.sub.Initial.view";
import View from "components/presentation/Presentation.view";

function Presentation() {

  const userId = sessionStorage.getItem("id");
  const isDeveloper = sessionStorage.getItem("is_developer") == "true";
  const { userUsage } = useUserUsage();

  const history = useHistory();
  const execModeRef = useRef(null);
  const chatRef = useRef(null);
  const notesRef = useRef(null);

  const [flowId, setFlowId] = useState(null);
  const [selectedNote, setSelectedNote] = useState(null);
  const [currentChat, setCurrentChat] = useState(null);
  const [chat, setChat] = useState(null);
  const [activeSection, setActiveSection] = useState({});

  const [callSetFlow] = useSet(SET_FLOW);


  const changeFlow = (flow) => {
    setFlowId(flow.id)

    localStorage.setItem("active_flow_id", flow.id)
    sessionStorage.setItem("execution_session_id",
      Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15));

    const views = (flow.presentationSettings?.views ?? []).filter(v => !v.is_hidden) // MISSING PERMS
    const defaultView = views.find(view => view.name == activeSection.name) ?? views[0]
    if (defaultView) setActiveSection({ name: defaultView.name })

    callInitChat({ flow_id: flow.id });
    callPermissions();

    if (flow.settings?.default_debug_execution == true)
      callSetFlow({
        id: flow.id,
        settings: {
          ...flow.settings,
          default_debug_execution: false,
        }
      });

    if ((flow.presentationSettings?.selected_lang ?? "auto") == "auto") {
      callSetFlow({
        id: flow.id,
        presentationSettings: {
          ...flow.presentationSettings,
          default_lang: (navigator.language || navigator.userLanguage || "en").split(/[-_.]/)[0]
        }
      });
    }
  }

  const reqFlow = useQuery(`{
    flows {
      name
      description
      token
      presentationSettings
      settings
      chatSettings
      theme {
        primaryColor
        accentColor
        logo
      }
    }
  }`, `is_current_version=True AND is_active=true`, {
    onCompleted: (data) => {
      if (data.flows.length == 0) return

      let defaultFlow = data.flows[0]
      for (let flow of data.flows)
        if (flow.id == localStorage.getItem("active_flow_id"))
          defaultFlow = flow
      changeFlow(defaultFlow)
    },
    orderBy: "name"
  });

  const reqUser = useDetail(`{
    user {
      firstName
      lastName
      email
      isAnonymous
    }
  }`, userId);

  const [callPermissions, reqPermissions] = useGetCall("/users/get_perms", { flow_id: flowId });

  const [callInitChat] = usePost("/chats/init_web_chat", {
    onCompleted: (data) => {
      setChat(data);
      setCurrentChat(data);
    },
  });

  const [callResumeChat] = usePost("/chats/resume_web_chat", {
    onCompleted: (data) => {
      setChat(data);
      setCurrentChat(data);
    },
  });


  useEffect(() => {
    if (localStorage.getItem("id") != null) { //Preload data from localStorage
      sessionStorage.setItem("token", localStorage.getItem("token"));
      sessionStorage.setItem("id", localStorage.getItem("id"));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshFlow = () => reqFlow.refetch();
  const refreshChat = () => reqFlow.refetch();

  const onClickTab = (name, args = {}) => setActiveSection({ name, ...args });

  const onClickChangeChat = (chat) => {

    if (chat) setChat(chat);
    else callInitChat({ flow_id: flow.id });

    setSelectedNote(null);
    setActiveSection({});

  }

  const onClickResumeChat = () => {
    setSelectedNote(null);
    callResumeChat({ flow_id: flow.id, chat_id: chat.id });
  }

  const onClickSelectNote = (note) => {
    setActiveSection({});
    setSelectedNote(note);
    if (note) setChat(note.message.chat);
  }

  const onClickLogo = () => {
    history.replace("/")
  }

  const onChangeFlow = (flowId) => {
    if (flowId == "create") return history.push("/create_flow")
    const flow = flows.find(flow => flow.id == flowId);
    changeFlow(flow)
  }

  const onCreateFlow = (flowId) => {
    reqFlow.refetch();
    changeFlow(flow)
  }

  if (reqFlow.loading || reqUser.loading || reqPermissions.loading) return <PlaceholderView />;
  if (reqFlow.error || reqUser.error || reqPermissions.error) return "";

  const { user = null } = reqUser.data || {};
  const { flows = [] } = reqFlow.data;

  if (flows.length == 0) return <InitialView />;

  const flow = flows.find(flow => flow.id == flowId);
  const permissions = reqPermissions.data || {};
  const isAdmin = permissions?.is_admin || false;
  const userRole = permissions?.role || null;

  if (!chat || (!userRole && !isAdmin)) return <PlaceholderView />;

  const chatSettings = flow.chatSettings || {};
  const chatPermissions = chatSettings.permissions || {};
  const showSidenav = chatPermissions.show_sidenav || {};
  const showShare = chatPermissions.show_share || {};
  const showManageCollections = chatPermissions.show_manage_collections || {};

  const presentationSettings = flow.presentationSettings || {};

  return (
    <View
      user={user}
      flows={flows}
      flow={flow}
      showSidenav={showSidenav}
      showShare={showShare}
      showManageCollections={showManageCollections}
      chatPermissions={chatPermissions}
      selectedNote={selectedNote}
      presentationSettings={presentationSettings}
      activeSection={activeSection}
      execModeRef={execModeRef}
      chatRef={chatRef}
      notesRef={notesRef}
      userRole={userRole}
      userUsage={userUsage}
      isAdmin={isAdmin}
      isDeveloper={isDeveloper}
      chat={chat}
      currentChat={currentChat}
      refreshFlow={refreshFlow}
      refreshChat={refreshChat}
      onClickChangeChat={onClickChangeChat}
      onClickResumeChat={onClickResumeChat}
      onClickSelectNote={onClickSelectNote}
      onClickTab={onClickTab}
      onClickLogo={onClickLogo}
      onChangeFlow={onChangeFlow}
      onCreateFlow={onCreateFlow}
    />);

}

const PlaceholderView = () =>
  <div id="presentation-mode" className="d-flex">
    <div id="presentation-sidenav"
      className="d-flex flex-row flex-sm-column h-100"></div>
    <div id="presentation-container" className="h-100 w-100">
      <div id="presentation-header"
        className={"d-flex w-100 justify-content-between align-items-center"}></div>
      <div id="presentation-content" className="pb-8">
        <Loading />
      </div>
    </div>
  </div>

export default Presentation;